// Theme management. Three modes: light / dark / night, plus "system" which
// follows the OS preference. Persisted to localStorage so the chosen theme
// survives reload before the /api/me/preferences round-trip completes.

import { signal, type Signal } from "@preact/signals";
import type { UiDesign } from "@juuplan/shared";

export type ThemeChoice = "light" | "dark" | "night" | "system";

const STORAGE_KEY = "juuplan:theme";

export function applyTheme(choice: ThemeChoice): void {
  const root = typeof document !== "undefined" ? document.documentElement : null;
  if (!root) return;
  const resolved = resolveTheme(choice);
  if (resolved === "light") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", resolved);
  }
}

export function resolveTheme(choice: ThemeChoice): "light" | "dark" | "night" {
  if (choice === "system") {
    const prefersDark =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-color-scheme: dark)").matches;
    return prefersDark ? "dark" : "light";
  }
  return choice;
}

export function loadThemeChoice(): ThemeChoice {
  if (typeof localStorage === "undefined") return "system";
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === "light" || raw === "dark" || raw === "night" || raw === "system") {
    return raw;
  }
  return "system";
}

export function saveThemeChoice(choice: ThemeChoice): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(STORAGE_KEY, choice);
  applyTheme(choice);
}

/**
 * Mount-time helper for main.tsx: read the persisted choice and apply it
 * immediately, before Preact hydrates, so there's no flash-of-light-theme.
 */
export function bootstrapTheme(): void {
  applyTheme(loadThemeChoice());
  // React to OS-level theme changes when the user chose "system".
  if (typeof window !== "undefined" && window.matchMedia) {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener?.("change", () => {
      if (loadThemeChoice() === "system") applyTheme("system");
    });
  }
}

// ---------------------------------------------------------------------------
// Design (Farbwelt + Schrift), unabhaengig von hell/dunkel. Seit 2026-10-27
// pro BENUTZER serverseitig gespeichert (`user_preferences.ui_design`,
// geliefert in GET /api/me, geschrieben per PATCH /api/me/preferences):
//   „classic" = bisheriges Design (Standard fuer alle),
//   „juunit"  = neues Design (gemeinsame juunIT-Spec).
// „Formular" ist ausgeblendet (CSS-Block bleibt in index.css stehen).
//
// Ablauf: main.tsx ruft bootstrapDesign() VOR dem ersten Zeichnen — das
// wendet den lokalen Cache an (kein Flackern). Sobald /api/me da ist, gilt der
// Serverwert (syncDesignFromServer, aufgerufen aus lib/auth.ts). Beim Logout
// bzw. ohne Anmeldung wird der Cache geloescht und classic gesetzt, damit nie
// das Design des vorherigen Benutzers stehen bleibt.
// ---------------------------------------------------------------------------

export type DesignChoice = UiDesign;

export const DEFAULT_DESIGN: DesignChoice = "classic";

/** Neuer Schluessel: der alte `juuplan:design` hatte „juunit" als Standard
 *  und wird bewusst NICHT uebernommen (Standard ist jetzt classic). */
const DESIGN_CACHE_KEY = "juuplan:ui-design";
const LEGACY_DESIGN_KEY = "juuplan:design";

/** Browserleisten-Farbe je Design (entspricht dem Wert in index.html). */
const THEME_COLOR: Record<DesignChoice, string> = {
  classic: "#2563EB",
  juunit: "#0D2A5C",
};

interface DesignCache {
  userId: number;
  design: DesignChoice;
}

export function isDesignChoice(v: unknown): v is DesignChoice {
  return v === "classic" || v === "juunit";
}

/** Globaler Designzustand — spiegelt immer das am <html> angewandte Design.
 *  Der Schalter in den Einstellungen liest DIESES Signal (nicht einmalig beim
 *  Oeffnen), damit ein spaeter eintreffendes /api/me ihn mitzieht. */
export const designSignal: Signal<DesignChoice> = signal<DesignChoice>(DEFAULT_DESIGN);

/** Weiche fuer die neuen Ablaeufe (UX-Vorschlaege P1–P7): sie gelten nur bei
 *  eingeschaltetem „Neues Design". Reaktiv, weil es das Signal liest — in
 *  Komponenten einfach `isNewUx()` aufrufen, Umschalten rendert neu. */
export function isNewUx(): boolean {
  return designSignal.value === "juunit";
}

export function applyDesign(choice: DesignChoice): void {
  designSignal.value = choice;
  const root = typeof document !== "undefined" ? document.documentElement : null;
  if (!root) return;
  // Immer explizit setzen: die Root-Tokens sind das neue Design, „classic"
  // haengt an :root[data-design="classic"].
  root.setAttribute("data-design", choice);
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute("content", THEME_COLOR[choice]);
}

export function currentDesign(): DesignChoice {
  if (typeof document === "undefined") return DEFAULT_DESIGN;
  const raw = document.documentElement.getAttribute("data-design");
  return isDesignChoice(raw) ? raw : DEFAULT_DESIGN;
}

function readCache(): DesignCache | null {
  try {
    const raw = localStorage.getItem(DESIGN_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DesignCache>;
    if (typeof parsed.userId !== "number" || !isDesignChoice(parsed.design)) return null;
    return { userId: parsed.userId, design: parsed.design };
  } catch {
    return null;
  }
}

function writeCache(cache: DesignCache): void {
  try {
    localStorage.setItem(DESIGN_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Privater Modus o. ae.: dann gilt nur der Serverwert nach /api/me.
  }
}

/** Design aus dem lokalen Cache (ohne Cache: classic). */
export function loadDesignChoice(): DesignChoice {
  return readCache()?.design ?? DEFAULT_DESIGN;
}

/** Wendet an und merkt sich den Wert fuer genau diesen Benutzer. */
export function saveDesignChoice(userId: number, choice: DesignChoice): void {
  writeCache({ userId, design: choice });
  applyDesign(choice);
}

/** Logout / keine Anmeldung: Cache weg, zurueck auf classic. */
export function clearDesignChoice(): void {
  try {
    localStorage.removeItem(DESIGN_CACHE_KEY);
  } catch {
    // ignore
  }
  applyDesign(DEFAULT_DESIGN);
}

// Laufende Schreibvorgaenge (PATCH). Solange einer laeuft, darf ein parallel
// eintreffendes /api/me (Tab-Fokus-Revalidierung) den optimistisch gesetzten
// Wert nicht mit dem alten Serverstand zurueckdrehen.
let pendingWrites = 0;

export function beginDesignWrite(): void {
  pendingWrites += 1;
}

export function endDesignWrite(): void {
  pendingWrites = Math.max(0, pendingWrites - 1);
}

/**
 * Serverwert aus GET /api/me anwenden. `user === null` = nicht angemeldet:
 * Cache loeschen und classic zeigen. Fehlt das Feld (aelterer Server), gilt
 * der Server-Standard classic.
 */
export function syncDesignFromServer(
  user: { id: number; ui_design?: unknown } | null,
): void {
  if (!user) {
    clearDesignChoice();
    return;
  }
  if (pendingWrites > 0) return;
  const design = isDesignChoice(user.ui_design) ? user.ui_design : DEFAULT_DESIGN;
  saveDesignChoice(user.id, design);
}

export function bootstrapDesign(): void {
  try {
    localStorage.removeItem(LEGACY_DESIGN_KEY);
  } catch {
    // ignore
  }
  applyDesign(loadDesignChoice());
}
